import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckSquare, Square, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Mapping {
    source: string;
    target: string;
}

interface MappingReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    mappings: Mapping[];
    onUpdate: (newMappings: Mapping[]) => void;
}

export const MappingReviewModal = ({ isOpen, onClose, mappings, onUpdate }: MappingReviewModalProps) => {
    const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

    // Reset selection when modal opens
    useEffect(() => {
        if (isOpen) setSelectedIndices([]);
    }, [isOpen]);

    const toggleSelection = (index: number) => {
        setSelectedIndices(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const toggleAll = () => {
        if (selectedIndices.length === mappings.length) {
            setSelectedIndices([]);
        } else {
            setSelectedIndices(mappings.map((_, i) => i));
        }
    };

    const handleBulkRemove = () => {
        const newMappings = mappings.filter((_, i) => !selectedIndices.includes(i));
        onUpdate(newMappings);
        setSelectedIndices([]);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="w-full max-w-2xl bg-white rounded-xl shadow-xl flex flex-col overflow-hidden max-h-[80vh]"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Review Mappings</h2>
                            <p className="text-xs text-gray-500">
                                {mappings.length} columns mapped
                            </p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Toolbar */}
                    <div className="px-6 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={toggleAll}
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
                            >
                                {selectedIndices.length === mappings.length && mappings.length > 0 ? (
                                    <CheckSquare className="h-4 w-4 text-purple-600" />
                                ) : (
                                    <Square className="h-4 w-4 text-gray-400" />
                                )}
                                Select All
                            </button>
                            {selectedIndices.length > 0 && (
                                <span className="text-purple-600 font-semibold bg-purple-100 px-2 py-0.5 rounded-full">
                                    {selectedIndices.length} Selected
                                </span>
                            )}
                        </div>
                        <Button
                            variant="destructive"
                            size="sm"
                            className="h-7 text-xs gap-2"
                            disabled={selectedIndices.length === 0}
                            onClick={handleBulkRemove}
                        >
                            <Trash2 className="h-3 w-3" />
                            Remove Selected
                        </Button>
                    </div>

                    {/* List */}
                    <div className="flex-1 overflow-auto p-0">
                        {mappings.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                                <p>No mappings found.</p>
                            </div>
                        ) : (
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-gray-500 uppercase bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-6 py-3 w-10"></th>
                                        <th className="px-6 py-3">Source Column</th>
                                        <th className="px-6 py-3 text-center w-8"></th>
                                        <th className="px-6 py-3">Target Column</th>
                                        <th className="px-6 py-3 w-20"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {mappings.map((m, idx) => (
                                        <tr
                                            key={idx}
                                            className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedIndices.includes(idx) ? 'bg-purple-50/50' : ''}`}
                                            onClick={() => toggleSelection(idx)}
                                        >
                                            <td className="px-6 py-3">
                                                {selectedIndices.includes(idx) ? (
                                                    <CheckSquare className="h-4 w-4 text-purple-600" />
                                                ) : (
                                                    <Square className="h-4 w-4 text-gray-300" />
                                                )}
                                            </td>
                                            <td className="px-6 py-3 font-medium text-gray-700">{m.source}</td>
                                            <td className="px-6 py-3 text-center text-gray-400">→</td>
                                            <td className="px-6 py-3 font-medium text-gray-900">{m.target}</td>
                                            <td className="px-6 py-3 text-right">
                                                <button
                                                    className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const newMappings = [...mappings];
                                                        newMappings.splice(idx, 1);
                                                        onUpdate(newMappings);
                                                    }}
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
